import React, { useState } from 'react';
import { ExternalLink, TrendingUp, TrendingDown, Minus, ChevronDown, ChevronUp, BookOpen, Trash2, Check, X } from 'lucide-react';

function CompetitorCard({ competitor, product, isSelected, onSelect, onCompare, onDeepDive, onRemove }) {
  const [expanded, setExpanded] = useState(false);
  const [confirmingRemove, setConfirmingRemove] = useState(false);
  
  const sentiment = competitor.sentiment_score;
  const sentimentColor = sentiment >= 0.85 ? '#10b981' : sentiment >= 0.5 ? '#f59e0b' : '#ef4444';
  const sentimentIcon = sentiment >= 0.85 ? <TrendingUp size={16} /> : sentiment >= 0.5 ? <Minus size={16} /> : <TrendingDown size={16} />;

  const handleRemoveClick = (event) => {
    event.stopPropagation();
    setConfirmingRemove(true);
  };

  const handleCancelRemove = (event) => {
    event.stopPropagation();
    setConfirmingRemove(false);
  };

  const handleConfirmRemove = (event) => {
    event.stopPropagation();
    onRemove?.(competitor.name);
  };

  return (
    <div style={{
      backgroundColor: 'white',
      borderRadius: '12px',
      padding: '16px',
      boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
      border: isSelected ? '2px solid #3b82f6' : '2px solid transparent',
      transition: 'all 0.2s',
      cursor: 'pointer',
    }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px' }}>
        <div style={{ flex: 1 }}>
          <h4 style={{ margin: '0 0 4px 0', fontSize: '16px', fontWeight: '600', color: '#111827' }}>
            {competitor.name}
          </h4>
          {competitor.website && (
            <a
              href={competitor.website}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '4px',
                fontSize: '12px',
                color: '#3b82f6',
                textDecoration: 'none',
              }}
            >
              Visit Website <ExternalLink size={12} />
            </a>
          )}
        </div>
        
        <div style={{ textAlign: 'right' }}>
          <div style={{ fontSize: '20px', fontWeight: '700', color: '#111827' }}>
            ${competitor.current_price?.toFixed(2) || 'N/A'}
          </div>
          <div style={{ fontSize: '12px', color: '#6b7280' }}>
            {competitor.currency || 'USD'}
          </div>
        </div>
      </div>

      {/* Sentiment Badge */}
      <div style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '6px',
        padding: '6px 12px',
        backgroundColor: sentimentColor + '15',
        borderRadius: '20px',
        marginBottom: '12px',
      }}>
        <span style={{ color: sentimentColor }}>{sentimentIcon}</span>
        <span style={{ fontSize: '13px', fontWeight: '600', color: sentimentColor }}>
          {(sentiment * 100).toFixed(0)}% Sentiment
        </span>
      </div>

      {/* Review Summary */}
      {competitor.review_summary && (
        <p style={{ fontSize: '13px', color: '#4b5563', margin: '0 0 12px 0', lineHeight: '1.5' }}>
          {competitor.review_summary}
        </p>
      )}

      {/* Key Features Preview */}
      {competitor.key_features && competitor.key_features.length > 0 && (
        <div style={{ marginBottom: '12px' }}>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
            {competitor.key_features.slice(0, 3).map((feature, idx) => (
              <span
                key={idx}
                style={{
                  fontSize: '11px',
                  padding: '4px 10px',
                  backgroundColor: '#f3f4f6',
                  color: '#374151',
                  borderRadius: '12px',
                }}
              >
                {feature}
              </span>
            ))}
            {competitor.key_features.length > 3 && (
              <span style={{ fontSize: '11px', color: '#6b7280', padding: '4px 0' }}>
                +{competitor.key_features.length - 3} more
              </span>
            )}
          </div>
        </div>
      )}

      {/* Actions */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginTop: '12px' }}>
        <button
          onClick={(event) => {
            event.stopPropagation();
            onSelect(competitor);
          }}
          title={isSelected ? 'Click to deselect' : 'Click to select for comparison'}
          style={{
            flex: 1,
            minWidth: '80px',
            padding: '8px 12px',
            fontSize: '13px',
            fontWeight: '500',
            backgroundColor: isSelected ? '#dbeafe' : '#f3f4f6',
            color: isSelected ? '#1e40af' : '#374151',
            border: 'none',
            borderRadius: '6px',
            cursor: 'pointer',
            transition: 'all 0.2s',
          }}
        >
          {isSelected ? '✓ Selected' : 'Select'}
        </button>

        {onDeepDive && (
          <button
            onClick={(event) => {
              event.stopPropagation();
              onDeepDive(competitor);
            }}
            title="Generate detailed deep dive analysis"
            style={{
              padding: '8px 12px',
              fontSize: '13px',
              fontWeight: '500',
              backgroundColor: '#eff6ff',
              color: '#1d4ed8',
              border: '1px solid #bfdbfe',
              borderRadius: '6px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '4px',
            }}
          >
            <BookOpen size={14} />
            Deep dive
          </button>
        )}
        
        <button
          onClick={(event) => {
            event.stopPropagation();
            setExpanded(!expanded);
          }}
          style={{
            padding: '8px 12px',
            fontSize: '13px',
            backgroundColor: '#f3f4f6',
            color: '#374151',
            border: 'none',
            borderRadius: '6px',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '4px',
          }}
        >
          {expanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
          Details
        </button>

        {onRemove && (
          confirmingRemove ? (
            <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
              <button
                type="button"
                onClick={handleConfirmRemove}
                title={`Remove ${competitor.name}`}
                style={{
                  padding: '8px 10px',
                  fontSize: '13px',
                  fontWeight: '500',
                  backgroundColor: '#fee2e2',
                  color: '#b91c1c',
                  border: '1px solid #fecaca',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px',
                }}
              >
                <Check size={14} />
                Remove
              </button>
              <button
                type="button"
                onClick={handleCancelRemove}
                title="Cancel removal"
                style={{
                  padding: '8px 10px',
                  fontSize: '13px',
                  fontWeight: '500',
                  backgroundColor: '#f3f4f6',
                  color: '#374151',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px',
                }}
              >
                <X size={14} />
                Cancel
              </button>
            </div>
          ) : (
            <button
              type="button"
              onClick={handleRemoveClick}
              title={`Remove ${competitor.name}`}
              style={{
                padding: '8px 10px',
                fontSize: '13px',
                backgroundColor: '#fef2f2',
                color: '#b91c1c',
                border: '1px solid #fecaca',
                borderRadius: '6px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
              }}
            >
              <Trash2 size={14} />
              Remove
            </button>
          )
        )}
      </div>

      {/* Expanded Details */}
      {expanded && (
        <div style={{ marginTop: '16px', paddingTop: '16px', borderTop: '1px solid #e5e7eb' }}>
          {/* Price Range */}
          {competitor.price_range && (
            <div style={{ marginBottom: '12px' }}>
              <div style={{ fontSize: '12px', fontWeight: '500', color: '#6b7280', marginBottom: '4px' }}>
                Historical Price Range
              </div>
              <div style={{ fontSize: '13px', color: '#374151' }}>
                ${competitor.price_range.min} - ${competitor.price_range.max}
              </div>
            </div>
          )}

          {/* Pros & Cons */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
            {competitor.pros && competitor.pros.length > 0 && (
              <div>
                <div style={{ fontSize: '12px', fontWeight: '500', color: '#10b981', marginBottom: '6px' }}>
                  Pros
                </div>
                <ul style={{ margin: 0, paddingLeft: '16px', fontSize: '12px', color: '#374151' }}>
                  {competitor.pros.map((pro, idx) => (
                    <li key={idx} style={{ marginBottom: '2px' }}>{pro}</li>
                  ))}
                </ul>
              </div>
            )}
            
            {competitor.cons && competitor.cons.length > 0 && (
              <div>
                <div style={{ fontSize: '12px', fontWeight: '500', color: '#ef4444', marginBottom: '6px' }}>
                  Cons
                </div>
                <ul style={{ margin: 0, paddingLeft: '16px', fontSize: '12px', color: '#374151' }}>
                  {competitor.cons.map((con, idx) => (
                    <li key={idx} style={{ marginBottom: '2px' }}>{con}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default CompetitorCard;
